<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Gestion des Litiges</h2>
          <p class="page-subtitle">{{ disputes.length }} litige{{ disputes.length !== 1 ? 's' : '' }} enregistré(s)</p>
        </div>
        <div class="header-actions">
          <el-input
            v-model="searchQ"
            placeholder="Nom, téléphone..."
            class="w-64"
            @input="loadDisputes"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          
          <el-select v-model="statusFilter" class="w-48" @change="loadDisputes">
            <el-option label="Tous les statuts" value="" />
            <el-option label="Ouverts" value="open" />
            <el-option label="Résolus (Client)" value="resolved_for_client" />
            <el-option label="Résolus (Artisan)" value="resolved_for_artisan" />
          </el-select>

          <el-button @click="loadDisputes" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <div v-if="selectedRows.length && canResolveDisputes" class="bulk-bar">
        <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
        <el-button type="success" size="small" :loading="bulkLoading" @click="bulkResolve('resolved_for_client')">Faveur Client</el-button>
        <el-button type="primary" size="small" :loading="bulkLoading" @click="bulkResolve('resolved_for_artisan')">Faveur Artisan</el-button>
      </div>

      <el-table :data="paged" v-loading="isLoading" class="custom-table" stripe @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="N° Litige" width="120">
          <template #default="{ $index }">
            <span class="font-display font-bold text-profissa-text-muted">#{{ String($index + 1 + (page - 1) * pageSize).padStart(4, '0') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Date d'ouverture" width="160" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
          <template #default="{ row }">
            <span class="text-profissa-text-muted text-sm">{{ fmtDate(row.created_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Motif / Description" min-width="250">
          <template #default="{ row }">
            <span class="text-sm">{{ truncate(row.reason, 60) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Statut" width="160" sortable :sort-method="(a, b) => (a.status || '').localeCompare(b.status || '')">
          <template #default="{ row }">
            <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                 :class="{
                   'bg-orange-500/10 text-orange-500 border border-orange-500/20': row.status === 'open',
                   'bg-green-500/10 text-green-500 border border-green-500/20': row.status === 'resolved_for_client',
                   'bg-blue-500/10 text-blue-500 border border-blue-500/20': row.status === 'resolved_for_artisan'
                 }">
              {{ statusLabel(row.status) }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="80" fixed="right" align="center">
          <template #default="{ row }">
            <ActionDropdown :actions="getActions(row)" @action="(key) => onAction(key, row)" />
          </template>
        </el-table-column>
      </el-table>

      <div class="flex justify-end pt-6 mt-4 border-t border-profissa-glass-border">
        <el-pagination
          v-if="disputes.length > pageSize"
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          :total="disputes.length"
          @size-change="page = 1"
        />
      </div>
    </el-card>

    <el-dialog append-to-body
      v-model="showDetail"
      :title="`Dossier Litige — ${selectedDispute ? fmtDate(selectedDispute.created_at) : ''}`"
      width="640px"
      custom-class="glass-dialog"
    >
      <template v-if="selectedDispute">
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Statut actuel</span>
            <div>
              <div class="px-3 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wide inline-block"
                   :class="{
                     'bg-orange-500/10 text-orange-500 border border-orange-500/20': selectedDispute.status === 'open',
                     'bg-green-500/10 text-green-500 border border-green-500/20': selectedDispute.status === 'resolved_for_client',
                     'bg-blue-500/10 text-blue-500 border border-blue-500/20': selectedDispute.status === 'resolved_for_artisan'
                   }">
                {{ statusLabel(selectedDispute.status) }}
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Ouvert par</span>
            <span class="font-bold">{{ selectedDispute.opened_by_label || 'Client' }}</span>
          </div>
          <div class="col-span-2 flex flex-col gap-1 mt-2">
            <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Motif du litige</span>
            <div class="p-4 rounded-xl bg-white/5 border border-profissa-glass-border text-sm leading-relaxed">
              {{ selectedDispute.reason }}
            </div>
          </div>
          <div v-if="selectedDispute.status !== 'open' && selectedDispute.admin_notes" class="col-span-2 flex flex-col gap-1 mt-2">
            <span class="text-xs font-bold text-profissa-accent uppercase tracking-wider">Notes de résolution</span>
            <div class="p-4 rounded-xl bg-profissa-accent/10 border border-profissa-accent/20 text-sm leading-relaxed text-profissa-text-main">
              {{ selectedDispute.admin_notes }}
            </div>
          </div>
        </div>

        <div v-if="selectedDispute.evidence_urls?.length" class="mb-6">
          <span class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider mb-2 block">Preuves jointes</span>
          <div class="flex flex-wrap gap-2">
            <a v-for="(url, i) in selectedDispute.evidence_urls" :key="i" :href="url" target="_blank" class="block overflow-hidden rounded-lg border border-profissa-glass-border hover:border-profissa-accent transition-colors">
              <el-image :src="url" :alt="'Preuve '+(i+1)" class="w-24 h-24 object-cover" :preview-src-list="selectedDispute.evidence_urls" :initial-index="i" />
            </a>
          </div>
        </div>

        <template v-if="selectedDispute.status === 'open'">
          <el-alert type="warning" show-icon :closable="false" class="mb-4 bg-orange-500/10 border border-orange-500/20 text-orange-500">
            <template #title>
              Lisez attentivement le motif et les preuves avant de statuer. La décision est irréversible.
            </template>
          </el-alert>
          <div class="flex flex-col gap-2 mb-4">
            <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Motif de décision (Requis)</label>
            <el-input v-model="resolutionNotes" type="textarea" :rows="3" placeholder="Expliquez votre raisonnement pour justifier la décision..." />
          </div>
          <div class="grid grid-cols-2 gap-3 pt-3 border-t border-profissa-glass-border">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-bold text-green-400 uppercase tracking-wider">Remboursement Client (FCFA)</label>
              <el-input-number v-model="clientRefundAmount" :min="0" :step="500" class="w-full" placeholder="0" />
              <span class="text-[0.65rem] text-profissa-text-dim">Virement LigdiCash automatique</span>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-bold text-blue-400 uppercase tracking-wider">Paiement Artisan (FCFA)</label>
              <el-input-number v-model="artisanPaymentAmount" :min="0" :step="500" class="w-full" placeholder="0" />
              <span class="text-[0.65rem] text-profissa-text-dim">Virement LigdiCash + gain enregistré</span>
            </div>
          </div>
        </template>
        <el-alert v-else type="success" title="Ce dossier est clôturé." :closable="false" class="bg-green-500/10 border border-green-500/20 text-green-500" />
      </template>

      <template #footer>
        <div class="flex justify-between items-center w-full">
          <div class="flex gap-2">
            <template v-if="selectedDispute?.status === 'open' && canResolveDisputes">
              <el-button type="success" :loading="isResolving" @click="resolve('resolved_for_client')">
                En faveur du Client
              </el-button>
              <el-button type="primary" :loading="isResolving" @click="resolve('resolved_for_artisan')">
                En faveur de l'Artisan
              </el-button>
            </template>
          </div>
          <el-button @click="showDetail = false">Fermer</el-button>
        </div>
      </template>
    </el-dialog>

    <el-dialog append-to-body v-model="showQuickResolve" title="Confirmer la résolution" width="420px" custom-class="glass-dialog">
      <p class="mb-4 text-sm leading-relaxed">
        Vous êtes sur le point de résoudre ce litige <strong class="text-profissa-accent">{{ quickResolution === 'resolved_for_client' ? 'en faveur du client' : "en faveur de l'artisan" }}</strong>.
      </p>
      <div class="flex flex-col gap-2 mb-4">
        <label class="text-xs font-bold text-profissa-text-muted uppercase tracking-wider">Motif de décision (Requis)</label>
        <el-input v-model="resolutionNotes" type="textarea" :rows="3" placeholder="Justifiez cette action..." />
      </div>
      <template #footer>
        <el-button @click="showQuickResolve = false">Annuler</el-button>
        <el-button
          :type="quickResolution === 'resolved_for_client' ? 'success' : 'primary'"
          :loading="isResolving"
          @click="resolve(quickResolution)"
        >
          Confirmer la décision
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, View, User, UserFilled } from '@element-plus/icons-vue'
import { extractApiError } from '../api'
import { listDisputes, resolveDispute } from '../services/adminApi'
import ActionDropdown from './shared/ActionDropdown.vue'
import { useAdminPermissions } from '../composables/useAdminPermissions'

const selectedRows    = ref([])
const disputes        = ref([])
const isLoading       = ref(false)
const searchQ         = ref('')
const statusFilter    = ref('')
const selectedDispute = ref(null)
const showDetail      = ref(false)
const isResolving     = ref(false)
const resolutionNotes = ref('')
const page            = ref(1)
const pageSize        = ref(15)
const showQuickResolve      = ref(false)
const quickResolution       = ref('')
const clientRefundAmount    = ref(0)
const artisanPaymentAmount  = ref(0)
const { hasPermission } = useAdminPermissions()
const canResolveDisputes = computed(() => hasPermission('disputes.resolve.any'))

const paged = computed(() => disputes.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value))
const onSelectionChange = (rows) => { selectedRows.value = rows }
const bulkLoading = ref(false)

const bulkResolve = async (resolution) => {
  if (!canResolveDisputes.value) return
  const open = selectedRows.value.filter(r => r.status === 'open')
  if (!open.length) { ElMessage.warning('Aucun litige ouvert parmi la sélection.'); return }
  const label = resolution === 'resolved_for_client' ? 'en faveur du client' : "en faveur de l'artisan"
  try {
    const { value: notes } = await ElMessageBox.prompt(`Résoudre ${open.length} litige(s) ${label}. Motif :`, 'Action groupée', {
      confirmButtonText: 'Confirmer', cancelButtonText: 'Annuler', inputType: 'textarea', inputPlaceholder: 'Motif de décision (requis)...',
      inputValidator: (v) => v?.trim() ? true : 'Le motif est obligatoire.',
    })
    bulkLoading.value = true
    let ok = 0
    for (const row of open) { try { await resolveDispute(row.id, { resolution, admin_notes: notes.trim() }); ok++ } catch { /* skip */ } }
    ElMessage.success(`${ok} litige(s) résolu(s).`)
    await loadDisputes()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const truncate = (v, n = 50) => String(v || '').length > n ? String(v).slice(0, n) + '…' : (v || '')
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'

const statusLabel   = (s) => ({ open: 'En cours', resolved_for_client: 'Gain de cause Client', resolved_for_artisan: 'Gain de cause Artisan' }[s] || s || '—')

const loadDisputes = async () => {
  isLoading.value = true
  try { disputes.value = await listDisputes(searchQ.value, statusFilter.value); page.value = 1 }
  catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const getActions = (row) => {
  const actions = [{ key: 'view', label: 'Voir le dossier', icon: View }]
  if (canResolveDisputes.value && row.status === 'open') {
    actions.push({ key: 'resolve_client', label: 'Faveur Client', icon: User, divided: true })
    actions.push({ key: 'resolve_artisan', label: 'Faveur Artisan', icon: UserFilled })
  }
  return actions
}

const onAction = (key, row) => {
  if (key === 'view') {
    selectedDispute.value = row
    resolutionNotes.value = ''
    clientRefundAmount.value = 0
    artisanPaymentAmount.value = 0
    showDetail.value = true
  } else if (key === 'resolve_client') {
    if (!canResolveDisputes.value) return
    selectedDispute.value = row
    resolutionNotes.value = ''
    clientRefundAmount.value = 0
    artisanPaymentAmount.value = 0
    quickResolution.value = 'resolved_for_client'
    showQuickResolve.value = true
  } else if (key === 'resolve_artisan') {
    if (!canResolveDisputes.value) return
    selectedDispute.value = row
    resolutionNotes.value = ''
    clientRefundAmount.value = 0
    artisanPaymentAmount.value = 0
    quickResolution.value = 'resolved_for_artisan'
    showQuickResolve.value = true
  }
}

const resolve = async (status) => {
  if (!canResolveDisputes.value) return
  if (!resolutionNotes.value.trim()) { ElMessage.error('Le motif de décision est obligatoire pour la traçabilité.'); return }
  isResolving.value = true
  try {
    const res = await resolveDispute(selectedDispute.value.id, {
      resolution: status,
      admin_notes: resolutionNotes.value.trim(),
      client_refund_amount: clientRefundAmount.value || 0,
      artisan_payment_amount: artisanPaymentAmount.value || 0,
    })
    const parts = [`Dossier clôturé — ${status === 'resolved_for_client' ? 'Client' : 'Artisan'}.`]
    if (res.client_refund_ref) parts.push(`Remboursement client réf. ${res.client_refund_ref}.`)
    if (res.artisan_payment_ref) parts.push(`Paiement artisan réf. ${res.artisan_payment_ref}.`)
    ElMessage.success(parts.join(' '))
    showDetail.value = false
    showQuickResolve.value = false
    await loadDisputes()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isResolving.value = false }
}

onMounted(loadDisputes)
</script>

<style scoped>
.page-container { @apply h-full flex flex-col; }
.main-list-card { @apply flex-1 flex flex-col; }
.list-header { @apply flex justify-between items-end pb-8; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }
.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }

:deep(.el-alert) {
  @apply rounded-xl border-none;
}
:deep(.el-alert__title) {
  @apply font-bold text-sm;
}
</style>
